/* global setTimeout, clearTimeout */
/**
 * WorkdaySheet.js
 *
 * Premium bottom sheet (NOT full screen) opened by the {@link module:components/workday/WorkdayHeaderPill}: when the
 * workday runs it shows the start time, worked-today and remaining / overtime, the snooze chips when the reminder is due,
 * "Munkanap lezárása" (HappyAlert confirm, with the "also stop the running timer" choice), "Időmérő megnyitása" and
 * "Mai napom". When idle it offers "Munkanap indítása". Hosted once by `WorkdayOverlay`.
 *
 * @module components/workday/WorkdaySheet
 */

import React from 'react';
import {Pressable, ScrollView, StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {WorkdaySheetShell} from './WorkdayShell';
import WorkdayRing from './WorkdayRing';
import {OutlineButton, PrimaryButton, SnoozeSegments, StatTile, WorkModeSelector, WorkdayControls} from './WorkdayParts';
import useWorkdayStopFlow from './useWorkdayStopFlow';
import {requestMyDayOpen} from '../../helpers/myDayBriefing';
import {BRAND, useManagementTheme, withAlpha} from '../../helpers/managementTheme';
import {useWorkday, useWorkdayLive} from '../../helpers/WorkdayContext';
import {openTimeTracker} from '../../helpers/workdayNav';
import {formatHM, formatHMS, isReminderDue, SNOOZE_OPTIONS} from '../../helpers/workdayRules';

const fmtClock = (iso) => (iso ? new Date(iso).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '--:--');

/**
 * @returns {JSX.Element|null} the status sheet (rendered while `surface === 'sheet'`).
 */
export default function WorkdaySheet() {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const {surface, state, busy, start, snooze, closeSheet} = useWorkday();
    const live = useWorkdayLive(1000);
    const {requestStop} = useWorkdayStopFlow();

    if (surface !== 'sheet' || !state) {
        return null;
    }

    const running = live.running;
    const paused = running && live.paused;
    const due = isReminderDue(state, Date.now());
    const overtime = live.overtimeSec > 0;
    const options = state.settings.snoozeOptions?.length ? state.settings.snoozeOptions : SNOOZE_OPTIONS;

    const onStop = async () => {
        // Close first: HappyAlert is its own modal and must not stack on top of this sheet.
        closeSheet();
        await new Promise((resolve) => setTimeout(resolve, 260));
        await requestStop();
    };

    const onStart = async () => {
        await start({});
        closeSheet();
    };

    const onTimer = () => {
        closeSheet();
        openTimeTracker();
    };

    const onBriefing = () => {
        closeSheet();
        setTimeout(() => requestMyDayOpen(), 260);
    };

    return (
        <WorkdaySheetShell visible onClose={closeSheet}>
            <LinearGradient colors={['#1f2a44', '#243656']} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.header}>
                <View style={styles.accentBar} />
                <View style={styles.headerIcon}>
                    <Icon name="briefcase-clock-outline" size={22} color="#FFFFFF" />
                </View>
                <View style={styles.headerText}>
                    <Text style={styles.kicker} numberOfLines={1}>{t('workday_kicker').toUpperCase()}</Text>
                    <Text style={styles.title} numberOfLines={1}>{t('workday_title')}</Text>
                </View>
                <View style={[styles.statusPill, {backgroundColor: paused ? 'rgba(245,158,11,0.32)' : (running ? 'rgba(249,115,22,0.28)' : 'rgba(255,255,255,0.14)')}]}>
                    {paused
                        ? <Icon name="pause" size={12} color="#FCD34D" />
                        : <View style={[styles.dot, {backgroundColor: running ? BRAND.light : '#94A3B8'}]} />}
                    <Text style={styles.statusText}>{paused ? t('workday_status_paused') : (running ? t('workday_status_running') : t('workday_status_idle'))}</Text>
                </View>
                <Pressable onPress={closeSheet} hitSlop={10} accessibilityRole="button" accessibilityLabel={t('myday_close')} style={styles.close}>
                    <Icon name="close" size={20} color="#FFFFFF" />
                </Pressable>
            </LinearGradient>

            <ScrollView contentContainerStyle={styles.body} showsVerticalScrollIndicator={false}>
                <View style={styles.ringRow}>
                    <WorkdayRing size={132} stroke={11} progress={live.progress} tone={live.tone} trackColor={withAlpha(m.colors.subText, 0.2)}>
                        <Text style={[styles.clock, {color: m.colors.heading}]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>
                            {running ? formatHMS(live.workedSec) : formatHM(live.workedSec)}
                        </Text>
                        <Text style={[styles.clockSub, {color: m.colors.subText}]} numberOfLines={1}>
                            {t('workday_target', {time: formatHM(live.targetSec)})}
                        </Text>
                    </WorkdayRing>
                </View>

                <View style={styles.tiles}>
                    <StatTile icon="clock-start" label={t('workday_started_at')} value={running ? fmtClock(state.running.startedAt) : '--:--'} />
                    <StatTile icon="timer-sand" label={t('workday_worked_today')} value={formatHM(live.workedSec)} accent={m.colors.info} />
                    {running && (
                        <StatTile icon={paused ? 'pause-circle-outline' : 'coffee-outline'} label={t('workday_break_time')} value={formatHM(live.breakSec)} accent="#F59E0B" />
                    )}
                    <StatTile
                        icon={overtime ? 'alarm-light-outline' : 'flag-checkered'}
                        label={overtime ? t('workday_overtime') : t('workday_remaining')}
                        value={formatHM(overtime ? live.overtimeSec : live.remainingSec)}
                        accent={overtime ? (live.tone === 'danger' ? '#DC2626' : '#F59E0B') : m.colors.success}
                    />
                </View>

                {running && due && (
                    <View style={styles.block}>
                        <Text style={[styles.blockTitle, {color: m.colors.heading}]}>{t('workday_snooze_title')}</Text>
                        <SnoozeSegments options={options} disabled={busy} onPick={snooze} />
                    </View>
                )}

                <WorkModeSelector />

                <View style={styles.block}>
                    {running
                        ? <WorkdayControls onStop={onStop} />
                        : <PrimaryButton label={t('workday_start')} icon="play-circle-outline" onPress={onStart} busy={busy} />}
                </View>

                <View style={styles.links}>
                    <OutlineButton flex icon="timer-outline" label={t('workday_open_timer')} onPress={onTimer} />
                    <OutlineButton flex icon="weather-sunset-up" label={t('workday_open_briefing')} onPress={onBriefing} />
                </View>
            </ScrollView>
        </WorkdaySheetShell>
    );
}

const styles = StyleSheet.create({
    header: {borderRadius: 22, paddingHorizontal: 14, paddingVertical: 14, flexDirection: 'row', alignItems: 'center', gap: 10, overflow: 'hidden'},
    accentBar: {position: 'absolute', top: 0, left: 0, right: 0, height: 3, backgroundColor: '#F59E0B'},
    headerIcon: {width: 42, height: 42, borderRadius: 14, backgroundColor: 'rgba(255,255,255,0.14)', alignItems: 'center', justifyContent: 'center'},
    headerText: {flex: 1},
    kicker: {color: 'rgba(226,236,255,0.72)', fontSize: 10.5, fontWeight: '800', letterSpacing: 1.1},
    title: {color: '#FFFFFF', fontSize: 20, fontWeight: '900', letterSpacing: -0.3},
    statusPill: {flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5},
    dot: {width: 7, height: 7, borderRadius: 4},
    statusText: {color: '#FFFFFF', fontSize: 11.5, fontWeight: '800'},
    close: {width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.14)', alignItems: 'center', justifyContent: 'center'},
    body: {paddingTop: 18, paddingBottom: 6, gap: 16},
    ringRow: {alignItems: 'center'},
    clock: {fontSize: 26, fontWeight: '900', letterSpacing: -0.6, fontVariant: ['tabular-nums'], maxWidth: 100},
    clockSub: {fontSize: 11.5, fontWeight: '700', marginTop: 2},
    tiles: {flexDirection: 'row', gap: 10},
    block: {gap: 10},
    blockTitle: {fontSize: 14, fontWeight: '900'},
    links: {flexDirection: 'row', gap: 10},
});
